import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Upload, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { ImportUsersModal } from '../~imports/ImportUsersModal';
import { BulkSendInvites } from './BulkSendInvites';
import { useOrgUserColumns } from './columns';
import { InviteUser } from './InviteUser';
import { UserFilters } from './UserFilters';

export const Route = createFileRoute('/admin-console/organisation-management/organisations/$orgId/users')({
    staticData: {
        presentation: 'fullPage',
        permissions: ['admin_users:read'],
        metadata: {
            title: 'Users',
        },
        ui: {
            icon: Users,
            title: 'Users',
        },
    },
    component: UsersInOrganization,
});

type QueryParams = RouterInputs['admin']['user']['listOrgUsers'];

function UsersInOrganization() {
    const { orgId } = Route.useParams();
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { isReadOnly } = useAdminModulePermissions('users');

    const [openInviteUserDialog, setOpenInviteUserDialog] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const deleteOrgUserMutation = trpc.admin.user.deleteOrgUser.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    // Toggle state for showing support accounts
    const [includeSupportAccounts, setIncludeSupportAccounts] = useState(false);

    // Query parameters with URL persistence (no orgId in the query params)
    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orgId,
    });

    const { data, isPending, refetch } = trpc.admin.user.listOrgUsers.useQuery({
        ...input,
        includeSupportAccounts,
    });

    const columns = useOrgUserColumns();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending || deleteOrgUserMutation.isPending,
        columns,
        rowKey: 'userId',
        queryParams: input,
        onParamsChange: (params) => setInput((prev) => ({ ...params, orgId: prev.orgId })),
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-org-users-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        rowSelection: {
            enabled: true,
            selectedRowKeys: selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
        },
    });

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="users" />

            {!isReadOnly && (
                <>
                    <InviteUser
                        open={openInviteUserDialog}
                        orgId={orgId}
                        onClose={() => {
                            setOpenInviteUserDialog(false);
                            void refetch();
                        }}
                        onInviteSuccess={(userId) => {
                            setOpenInviteUserDialog(false);
                            void refetch();
                            navigate({
                                to: '/admin-console/organisation-management/organisations/$orgId/users/edit/$userId',
                                params: { orgId: orgId, userId: userId },
                            });
                        }}
                    />

                    <ImportUsersModal
                        open={importModalOpen}
                        orgId={BigInt(orgId)}
                        onClose={() => setImportModalOpen(false)}
                        onSuccess={() => {
                            void refetch();
                        }}
                    />
                </>
            )}

            <TableToolbar
                filters={
                    <UserFilters
                        orgId={BigInt(orgId)}
                        onChange={(where) => setInput((prev) => ({ ...prev, where }))}
                        includeSupportAccounts={includeSupportAccounts}
                        onIncludeSupportAccountsChange={setIncludeSupportAccounts}
                    />
                }
                variant="enhanced"
                actions={
                    !isReadOnly ? (
                        <>
                            <BulkSendInvites
                                selectedRowKeys={selectedRowKeys}
                                users={data?.rows}
                                orgId={BigInt(orgId)}
                                onSuccess={() => {
                                    setSelectedRowKeys([]);
                                    void refetch();
                                }}
                            />
                            <Button type="secondary" icon={<Upload />} onClick={() => setImportModalOpen(true)}>
                                Bulk Import
                            </Button>
                            <Button type="primary" icon={<UserPlus />} onClick={() => setOpenInviteUserDialog(true)}>
                                Invite user
                            </Button>
                        </>
                    ) : undefined
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={(item) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        // Don't navigate if clicking on checkbox
                        const target = e.target as HTMLElement;
                        if (target.closest('.ant-checkbox-wrapper')) {
                            return;
                        }
                        navigate({
                            to: '/admin-console/organisation-management/organisations/$orgId/users/edit/$userId',
                            params: { orgId, userId: item.userId },
                        });
                    },
                })}
            />
            <Outlet />
        </PageController>
    );
}
